/**
 * Real-shipment → `Campus` adapter for the chef orders surfaces.
 *
 * The orders + shopping-list pages (and the print packet) render the
 * chefs-v2 `Campus` tree. With the mock fixtures retired, this module
 * projects `KitchenShipmentSummary` rows (GET /api/kitchen-orders/
 * shipments) into that shape:
 *
 *   - One `Campus` per destination school, aggregated across every
 *     shipment in the input (pass a single day's shipments to get a
 *     per-day campus — see `shipmentsForDay`).
 *   - One `Deliverable` per PACKAGE (a `BundleDeliverable` — a
 *     temperature-grouped set of components a chef bags together, e.g.
 *     "Chicken • Mac & Cheese" HOT vs "Apple" AMBIENT). The row's count is
 *     the package count (the meal's order count); its `components[]` are
 *     the items in the package, each carrying its own recipe so the
 *     detail panel renders one recipe section per item. IDENTICAL
 *     packages (same components + variants + serving sizes + temperature)
 *     merge across meals/days, summing the package count (option A).
 *   - `MealtimeBlock.meals` — the TRUE meal count, tallied per shipment as
 *     packages are added, i.e. BEFORE the merge above. That merge is right for
 *     prep quantities but collapses bundle identity, the service-date axis and
 *     the bundle/standalone split, so meals CANNOT be recovered from the
 *     rendered rows afterwards. See the tally in `shipmentsToCampuses`.
 *   - `CampusInfo` from the school Location's Address row (delivery
 *     window, contact, parking). Fields the backend doesn't model yet
 *     (lunchStart, notes) render as ''.
 *
 * Known degradations vs the old mock tree (follow-ups, not bugs):
 *   - `orderStatus` is synthesized as submitted-on-service-date —
 *     real order-deadline data is a separate axis; the missed-deadline
 *     banner never fires from adapted campuses.
 *
 * Pure — bun test imports it directly (workspace `@ordo/constants` resolves
 * under bun test; everything else stays relative).
 */

import {
  buildBundleAutoTitle,
  buildBundleCondimentDescription,
  bundleTitleKindRank,
} from '@ordo/constants/bundle-title'
import { milkOptionLabel, normalizeMilkOptionCode, orderedMilkOptionCodes } from '@ordo/constants/milk-options'
import {
  bulkPackingGroupKey,
  type BulkPackingPlan,
  type BulkPackingSpec,
  mergeBulkPackingPlans,
  planBulkPacking,
} from '@ordo/constants/packing-plan'
import { shipmentDayKey } from '@ordo/constants/shipment-date-axis'

import type {
  KitchenLineRecipe,
  KitchenShipmentLineSummary,
  KitchenShipmentSummary,
  SpecialDietItem,
} from '../../api/kitchen-orders'
import { type DeliverableSectionPlan, deliverableSectionsKey, planDeliverableSections } from './deliverable-sections'
import { formatRecipeServing } from './format'
import { compareServiceMaterials, mergeSupplyLines, supplyLinesFrom, tallySupplyLine } from './service-materials'
import type {
  Campus,
  CampusMilkGroupQty,
  CampusMilkLine,
  CampusMilkMealtimeQty,
  Component,
  ComponentPortion,
  Deliverable,
  DeliverableSection,
  Diet,
  DryRunServiceType,
  Mealtime,
  MealtimeBlock,
  MealtimeGroup,
  PortionTier,
  ProductType,
  Recipe,
  SupplyLine,
  Temperature,
} from './types'
import {
  componentKindToProductType,
  MEALTIME_LABEL,
  MEALTIME_ORDER,
  toDiets,
  UNASSIGNED_GROUP_KEY,
  UNASSIGNED_GROUP_LABEL,
} from './types'

/**
 * Server mealtime enum → the client's lowercase `Mealtime` axis.
 * COMPLETE over the Prisma `Mealtime` enum — AM_SNACK/PM_SNACK shipments
 * used to be absent here and the `?? 'lunch'` fallback silently merged
 * their deliverables into the Lunch block's counts. The generic `SNACK`
 * keeps its historical pm-snack placement.
 */
const SERVER_MEALTIME_TO_CLIENT: Record<string, Mealtime> = {
  BREAKFAST: 'breakfast',
  AM_SNACK: 'am-snack',
  LUNCH: 'lunch',
  SNACK: 'pm-snack',
  PM_SNACK: 'pm-snack',
  SUPPER: 'supper',
}

/** Resolve a wire mealtime; an UNKNOWN value logs once instead of merging into Lunch. */
function clientMealtime(wire: string): Mealtime {
  const mapped = SERVER_MEALTIME_TO_CLIENT[wire]
  if (mapped) {
    return mapped
  }
  if (!warnedUnknownMealtimes.has(wire)) {
    warnedUnknownMealtimes.add(wire)
    console.error(
      `[chefs] unknown shipment mealtime "${wire}" — rendering under Lunch; update SERVER_MEALTIME_TO_CLIENT`,
    )
  }
  return 'lunch'
}
const warnedUnknownMealtimes = new Set<string>()

function temperatureFor(tc: KitchenShipmentLineSummary['temperatureClass']): Temperature {
  if (tc === 'HOT') {
    return 'hot'
  }
  return tc === 'AMBIENT' ? 'ambient' : 'chilled'
}

/** Map the wire recipe onto the client `Recipe` shape. */
function toClientRecipe(wire: KitchenLineRecipe | null): Recipe | undefined {
  if (!wire) {
    return undefined
  }
  return {
    id: wire.id,
    baseYield: wire.baseYield,
    yieldUnit: wire.yieldUnit,
    ingredients: wire.ingredients.map((i) => ({
      id: i.id,
      name: i.name,
      quantity: i.quantity,
      unit: i.unit,
      quantityBasis: i.quantityBasis,
      gramsPerCup: i.gramsPerCup ?? null,
      maxQuantityValue: i.maxQuantityValue ?? null,
      maxQuantityUnit: i.maxQuantityUnit ?? null,
      scalingNotes: i.scalingNotes ?? null,
      brand: i.brand,
      ingredientId: i.ingredientId,
      brandOptionId: i.brandOptionId,
      brandSource: i.brandSource,
      brandSelectable: i.brandSelectable,
      brandOptionCount: i.brandOptionCount,
    })),
    steps: wire.steps.map((s) => ({
      id: s.id,
      text: s.text,
      ingredientRefs: s.ingredientRefs.map((r) => ({
        name: r.name,
        quantity: r.quantity,
        unit: r.unit,
        quantityBasis: r.quantityBasis,
      })),
    })),
    haccp: wire.haccp.map((point) => ({ id: point.id, text: point.text })),
    tips: wire.tips.map((tip) => ({ id: tip.id, text: tip.text })),
  }
}

/** One item inside a package (a component leaf line), pre-projection. */
interface PackageComponentBucket {
  disherSize?: string | null
  componentId: string
  componentVariantId: string | null
  name: string
  componentKind: string | null
  servingSize: string | null
  /** Pre-formatted RECIPE-based serving ("2 Pancakes", "1/2 Cup") — PROD-173. */
  recipeServing: string | null
  /** The same serving as numbers, for the deliverable copy's totals. */
  portion: ComponentPortion | null
  imageUrl: string | null
  diets: string[]
  allergens: string[]
  allergensComplete: boolean
  recipe: KitchenLineRecipe | null
  /** Serving-size scale for this line's ingredients; 1 = recipe primary. */
  servingMultiplier: number
  /** `Component.milkOptionCode` — non-null marks a milk item (PROD-1502). */
  milkOptionCode: string | null
  /**
   * How many of this CONDIMENT ride per meal — the leaf's serving count over
   * the package count (2 ketchup packets on a 100-meal order = 200/100 = 2).
   * Undefined on non-condiments.
   *
   * Computed BEFORE identical packages merge, and deliberately so: the merge
   * sums `qty` across packages that share a composition, so a ratio taken
   * after it would divide one package's leaf by every package's total. The
   * per-meal figure is identical across merged rows by construction (same
   * composition), which is what makes the pre-merge value the correct one to
   * keep.
   */
  condimentPerMeal?: number
}

/** A package = a BundleDeliverable: temperature-grouped items bagged together. */
interface PackageBucket {
  rowKey: string
  mealtime: Mealtime
  /** Order-group section (PROD-1296) — `shipmentGroupKey` of the shipment
   * the package came from; null on an ungrouped shipment. Part of the row
   * key, so a Preschool apple and a K-8 apple never share a row. */
  groupKey: string | null
  groupLabel: string | null
  /** Composed label of the package's contents via the canonical bundle
   * auto-title system (kind order, ' & ' within a kind, ' • ' between kinds,
   * beverages/milk last), e.g. "Cheeseburger • Roasted Potato Wedges". */
  name: string
  /** "Condiments: X, Y" companion line (condiments are excluded from the
   * title by the shared builder), null when the package has none. */
  condiments: string | null
  temperatureClass: KitchenShipmentLineSummary['temperatureClass']
  /** Package/serving count — summed across identical packages. Bulk lines
   * contribute their SERVING count. */
  qty: number
  /** Deliverable packaging label (PROD-174): first non-null `packagingSkuName`
   * among the deliverable's PACKAGE lines, else the deliverable's name. */
  packaging: string | null
  /** Reference photo for the SKU `packaging` names (PROD-1216). Read off the
   * SAME line the label came from, so the picture always matches the words —
   * a sibling line's photo next to another SKU's name is worse than none. */
  packagingImageUrl: string | null
  /** `Ingredient.packagingSkuKind` of that same SKU (INDIVIDUAL / BULK / …). */
  packagingSkuKind: string | null
  /** The inner packages and what each holds (`planDeliverableSections`); null when none. Part of the row key. */
  sections: DeliverableSectionPlan[] | null
  components: PackageComponentBucket[]
  /** The package belongs to a field-trip (bagged) order — see `lineIsFieldTrip`. */
  isFieldTrip: boolean
  /** The package is a derived holiday treat — see `lineIsHolidayTreat`. */
  isHolidayTreat: boolean
  /** The package came from a chef DRY RUN shipment — a pre-launch test cook. */
  isDryRun: boolean
  /** TASTING vs DRY_RUN when `isDryRun` — names the row badge. */
  dryRunServiceType: DryRunServiceType | null
  /** Straight-serve (bulk) packaging — one pan serving many, not plated. */
  isBulkPrep: boolean
  /** The shipment's menus deliver it cold, reheated on site (`KitchenShipmentSummary.deliverCold`). */
  deliverCold: boolean
  /** Supplies riding this package (isSupply PACKAGE lines), summed per SKU. */
  supplies: SupplyLine[]
  /** Grade tier of this package's portion — unanimous across its leaves (a
   * row merges lines of ONE portion), null when they carry none or disagree. */
  portionTier: PortionTier | null
}

const FIELD_TRIP_TAG_SLUG = 'field-trip'

/** Parsed `quantityBasis` off a PACKAGE line, either service mode. */
export interface PackageQuantityBasis {
  /**
   * The materializer writes the aggregate's order count here for BOTH modes,
   * so this IS the number of meals the line was built for. Prefer it over
   * `plannedQuantity` on NON-PRIMARY packaging lines, where that column holds
   * a compounded child count. (Primary lines now agree: quantities are
   * servings for bulk and plated alike.)
   */
  servingCount: number
}

/**
 * Parse a wire `quantityBasis` JSON, or null when the line carries none.
 *
 * A non-null result also SUGGESTS the line is primary: the materializer
 * writes `quantityBasis` only for `planned.isPrimary`. Treat it as a hint,
 * not the marker — rows predating the column carry none, and callers must
 * first narrow to ROOT lines via `parentLineId`, which states exactly what a
 * parsed basis can only imply.
 */
export function parseQuantityBasis(quantityBasis: unknown): PackageQuantityBasis | null {
  if (!quantityBasis || typeof quantityBasis !== 'object') {
    return null
  }
  const o = quantityBasis as Record<string, unknown>
  const mode = o['mode']
  if (typeof mode !== 'string') {
    return null
  }
  const servingCount = o['servingCount']
  if (typeof servingCount !== 'number' || !Number.isFinite(servingCount) || servingCount <= 0) {
    return null
  }
  return { servingCount }
}

/**
 * Field-trip signal for one wire line: the per-line flag frozen at
 * materialize time (any bundle picked in the field-trip planner), with the
 * legacy catalog-global bundle tag as fallback for lines that predate it.
 */
function lineIsFieldTrip(line: KitchenShipmentLineSummary): boolean {
  return line.isFieldTrip === true || (line.bundleTagSlugs ?? []).includes(FIELD_TRIP_TAG_SLUG)
}

/** Derived holiday treat (free with every meal) — packed, badged, never a meal. */
function lineIsHolidayTreat(line: KitchenShipmentLineSummary): boolean {
  return (line.holidayTreatMenuItemId ?? null) !== null
}

/** Stable identity for a single component within a package's merge key. */
/**
 * Stable identity for a single component within a package's merge key. Carries
 * BOTH portion axes: the grade-tier `servingSize`, and the RECIPE portion (the
 * deliverable pick's serving-size option) that the row renders and scales its
 * ingredients by. A merged row keeps the first package's components, so a
 * portion missing from this key is silently overwritten: Howard Street's meat
 * and veggie meals ship one mac at 1/2 Cup and 1 Cup, and the two packages
 * merged into ONE 90-count row reading "1/2 Cup" at the 1/2 Cup multiplier
 * (PROD-1372).
 */
function componentIdentity(line: KitchenShipmentLineSummary): string {
  return `${line.componentId}|${line.componentVariantId ?? ''}|${line.servingSize ?? ''}|${line.recipeServingQuantity ?? ''}|${line.recipeServingUnit ?? ''}|${line.servingMultiplier ?? 1}`
}

/**
 * Portion-agnostic identity — the same component at two grade-tier portions is
 * ONE item. Used to dedupe bundle-deliverable groups, whose leaves are split
 * per grade tier (each carrying that tier's `servingLabel` as `servingSize`),
 * so a two-tier deliverable renders "Corn" rather than "Corn, Corn". `solo:`
 * groups keep the full `componentIdentity` — they are KEYED by portion (see
 * the group-key comment below), so collapsing their identities would merge two
 * distinct portions of one item into a single row.
 */
function componentIdentityNoPortion(line: KitchenShipmentLineSummary): string {
  return `${line.componentId}|${line.componentVariantId ?? ''}`
}

/**
 * Package-level product type: the main entree if the package has one, else the
 * first item's type (the detail panel shows one "Type" for the package).
 */
function packageProductType(productTypes: ReadonlyArray<ProductType>): ProductType {
  return productTypes.find((t) => t === 'main') ?? productTypes[0] ?? 'main'
}

/** Product types of a package's items, pre-projection. */
function packageComponentTypes(components: ReadonlyArray<PackageComponentBucket>): ProductType[] {
  return components.map((c) => componentKindToProductType(c.componentKind))
}

function toPackageComponent(line: KitchenShipmentLineSummary, packageCount: number): PackageComponentBucket {
  return {
    componentId: line.componentId!,
    componentVariantId: line.componentVariantId,
    name: line.componentName ?? 'Item',
    componentKind: line.componentKind,
    servingSize: line.servingSize,
    disherSize: line.disherSize,
    recipeServing: formatRecipeServing(line.recipeServingQuantity, line.recipeServingUnit),
    portion:
      line.recipeServingQuantity !== null &&
      line.recipeServingQuantity !== undefined &&
      Number.isFinite(line.recipeServingQuantity) &&
      line.recipeServingQuantity > 0 &&
      line.recipeServingUnit
        ? {
            quantity: line.recipeServingQuantity,
            unit: line.recipeServingUnit,
            ...(line.recipeServingName ? { name: line.recipeServingName } : {}),
          }
        : null,
    imageUrl: line.componentImageUri,
    diets: line.diets ?? [],
    allergens: line.allergens ?? [],
    allergensComplete: line.allergensComplete === true,
    recipe: line.recipe,
    servingMultiplier: line.servingMultiplier ?? 1,
    milkOptionCode: line.milkOptionCode ?? null,
    ...(line.componentKind === 'CONDIMENT' ? { condimentPerMeal: condimentPerMeal(line, packageCount) } : {}),
  }
}

/**
 * Per-meal count for one condiment leaf. Floors at 1: a chef row reading "×0"
 * would tell the kitchen to pack nothing while the package still ships, and a
 * fractional result (a shared dipping cup across two meals) rounds to the
 * nearest whole packet because packets are not divisible. Pure — unit-tested.
 */
export function condimentPerMeal(
  line: Pick<KitchenShipmentLineSummary, 'plannedQuantity'>,
  packageCount: number,
): number {
  if (packageCount <= 0) {
    return 1
  }
  return Math.max(1, Math.round(line.plannedQuantity / packageCount))
}

/** Diets that hold for EVERY component (a package is "X-free" iff all items are). */
function intersectDiets(perComponent: Diet[][]): Diet[] {
  if (perComponent.length === 0) {
    return []
  }
  return perComponent.reduce((acc, diets) => acc.filter((d) => diets.includes(d)))
}

/**
 * Page-unique id of a projected row. The row key alone is unique only WITHIN
 * one campus: two schools on the same menu produce byte-identical row keys
 * (same component, portion, temperature, packaging), and /orders/kitchen
 * re-finds the selected row by id across EVERY campus (`findDeliverableById`),
 * so a shared key opened the other school's panel — a 39-meal campus showed
 * the 355x of its sibling, and both rows lit up as selected (PROD-1341).
 */
export function deliverableRowId(campusId: string, rowKey: string): string {
  return `${campusId}|${rowKey}`
}

/** Project one package item into the chef-UI `Component` (recipe included). */
function bucketToComponent(c: PackageComponentBucket): Component {
  return {
    id: c.componentId,
    name: c.name,
    productType: componentKindToProductType(c.componentKind),
    diets: toDiets(c.diets),
    ...(c.allergens.length > 0 ? { allergens: c.allergens } : {}),
    ...(c.allergensComplete ? { allergensComplete: true } : {}),
    recipe: toClientRecipe(c.recipe),
    servingMultiplier: c.servingMultiplier,
    ...(c.portion ? { portion: c.portion } : {}),
    ...(c.servingSize ? { portionLabel: c.servingSize } : {}),
    ...(c.disherSize ? { disherSize: c.disherSize } : {}),
    ...(c.imageUrl ? { imageUrl: c.imageUrl } : {}),
    ...(c.milkOptionCode ? { milkOptionCode: c.milkOptionCode } : {}),
    // Condiment components render as compact ×N rows in the detail panel /
    // print (never full recipe sections) and are excluded from the title. The
    // ×N is `condimentQty`, which had no writer anywhere in the app before
    // this — every reader fell through to `?? 1`, so a 2-packet condiment
    // always read ×1.
    ...(c.componentKind === 'CONDIMENT'
      ? { isCondiment: true, ...(c.condimentPerMeal !== undefined ? { condimentQty: c.condimentPerMeal } : {}) }
      : {}),
  }
}

/** Project a merged package bucket into the chef-UI `Deliverable` for `campusId`. */
function projectPackage(p: PackageBucket, campusId: string): Deliverable {
  // Components in the same canonical order the auto-title uses (kind rank,
  // ties keep leaf order) so the detail panel sections, print rows, and the
  // per-serving sublabel all read in title order.
  const orderedBuckets = p.components
    .map((c, index) => ({ c, index }))
    .sort((left, right) => {
      const rankDelta =
        bundleTitleKindRank(left.c.componentKind, left.c.name) -
        bundleTitleKindRank(right.c.componentKind, right.c.name)
      return rankDelta !== 0 ? rankDelta : left.index - right.index
    })
    .map(({ c }) => c)
  const components: Component[] = orderedBuckets.map(bucketToComponent)
  const productType: ProductType = packageProductType(components.map((c) => c.productType))
  const diets = intersectDiets(components.map((c) => c.diets))
  // Package image: ONLY a single-item package shows its component's photo.
  // A multi-item package has no deliverable-level image on this wire, and
  // leading with ONE component's photo misrepresents the whole package —
  // render the placeholder instead (Sheila 2026-08-03). Condiments don't
  // count toward "multi" (they're excluded from the title for the same
  // reason), so "entrée + ranch cup" still shows the entrée.
  const imageBuckets = orderedBuckets.filter((c) => c.componentKind !== 'CONDIMENT')
  const soleItem =
    imageBuckets.length === 1 ? imageBuckets[0] : imageBuckets.length === 0 ? (orderedBuckets[0] ?? null) : null
  const imageUrl = soleItem?.imageUrl ?? null
  // The row shows RECIPE-based servings (PROD-173) — the grade-tier wire
  // `servingSize` stays merge-key-only and never renders. A multi-item
  // package lists EVERY item's serving, in title order, joined with ' + ';
  // items with no recipe serving are skipped, and condiments stay out (they
  // live on the condiments sublabel) unless the package is all condiments.
  const servingBuckets = orderedBuckets.filter(
    (c) => c.componentKind !== 'CONDIMENT' || orderedBuckets.every((o) => o.componentKind === 'CONDIMENT'),
  )
  // A multi-item package NAMES the item each serving belongs to
  // ("Turkey Sausage: 2 Links + Pancakes: 4 Pancakes") — a bare
  // "2 Links + 4 Pancakes" leaves the chef guessing which portion is which,
  // and worse when both are volumes ("1 1/2 Quesadilla + 1/2 cup"). A
  // single-item package stays unlabelled: the row title already names it.
  // Labelling keys off the ITEM count, not the serving count, so a package
  // where only one item carries a recipe serving still says which item that
  // portion is for.
  const labelServings = servingBuckets.length > 1
  const servingParts = servingBuckets
    .filter((c): c is PackageComponentBucket & { recipeServing: string } => Boolean(c.recipeServing))
    .map((c) => (labelServings ? `${c.name}: ${c.recipeServing}` : c.recipeServing))
  const servingSize = servingParts.length > 0 ? servingParts.join(' + ') : null
  const bucketById = new Map(orderedBuckets.map((c) => [c.componentId, c]))
  const sections: DeliverableSection[] | null = p.sections
    ? p.sections.map((section) => ({
        packaging: section.packaging,
        perMeal: section.perMeal,
        items: section.componentIds.map((id) => ({
          name: bucketById.get(id)?.name ?? 'Item',
          serving: bucketById.get(id)?.recipeServing ?? null,
        })),
      }))
    : null
  return {
    ...(sections ? { sections } : {}),
    ...(p.supplies.length > 0 ? { supplies: p.supplies } : {}),
    ...(p.groupKey !== null && p.groupLabel !== null ? { groupKey: p.groupKey, groupLabel: p.groupLabel } : {}),
    id: deliverableRowId(campusId, p.rowKey),
    // Field-trip packages carry the bagged copy in the name itself so every
    // surface (rows, detail panel, print) reads "Bagged Lunch: …" — the milk
    // package included, since the whole order travels in the bags.
    name: p.isFieldTrip ? `Bagged ${MEALTIME_LABEL[p.mealtime]}: ${p.name}` : p.name,
    ...(p.condiments ? { condiments: p.condiments } : {}),
    kind: components.length > 1 ? 'bundle' : 'standalone',
    productType,
    qty: p.qty,
    unit: 'x',
    bulk: p.isBulkPrep,
    temperature: temperatureFor(p.temperatureClass),
    ...(p.deliverCold ? { deliverCold: true } : {}),
    ...(servingSize ? { servingSize } : {}),
    ...(p.packaging ? { packaging: p.packaging } : {}),
    ...(p.packagingSkuKind ? { packagingSkuKind: p.packagingSkuKind } : {}),
    ...(p.packagingImageUrl ? { packagingImageUrl: p.packagingImageUrl } : {}),
    ...(imageUrl ? { imageUrl } : {}),
    diets,
    components,
    mealtime: p.mealtime,
    ...(p.isFieldTrip ? { isFieldTrip: true } : {}),
    ...(p.isHolidayTreat ? { isHolidayTreat: true } : {}),
    ...(p.isDryRun ? { isDryRun: true, dryRunServiceType: p.dryRunServiceType ?? 'DRY_RUN' } : {}),
    ...(p.portionTier ? { portionTier: p.portionTier } : {}),
  }
}

/**
 * The one portion tier every leaf agrees on, else null. Lines from two tiers
 * that share a portion (or legacy lines with no stamp) leave the row untiered
 * — a wrong tag is worse than the serving-size sublabel alone.
 */
function unanimousPortionTier(leaves: ReadonlyArray<KitchenShipmentLineSummary>): PortionTier | null {
  let tier: PortionTier | null = null
  for (const leaf of leaves) {
    const lineTier = leaf.portionTier ?? null
    if (!lineTier) {
      return null
    }
    if (tier === null) {
      tier = { code: lineTier.code, label: lineTier.label }
    } else if (tier.code !== lineTier.code) {
      return null
    }
  }
  return tier
}

/**
 * Portion-agnostic identity of a projected row — the same item at two
 * grade-tier portions shares it. Component ids plus the row-splitting flags
 * that already give a row a distinct title or section (group, bagged, bulk,
 * dry run), so two rows share an identity only when a packer would otherwise
 * read two identical titles.
 */
export function deliverableIdentityNoPortion(d: Deliverable): string {
  const flags = [
    d.groupKey ?? '',
    d.temperature,
    d.isFieldTrip ? 'ft' : '',
    d.isHolidayTreat ? 'treat' : '',
    d.bulk ? 'bulk' : '',
    d.isDryRun ? `dry:${d.dryRunServiceType ?? 'DRY_RUN'}` : '',
  ]
  const items = d.components.map((c) => c.id).sort()
  return `${flags.join('|')}|${items.join(',')}`
}

/**
 * Tag the rows of ONE mealtime block that need a portion-tier label: rows
 * whose portion-agnostic identity occurs more than once in the block (the
 * same meal at K-8 and 9-12 portions — two SKUs the packer must tell apart).
 * Rows with a unique identity are returned untouched, so a single-tier campus
 * renders exactly as before. Within a clashing set, a row with a resolvable
 * tier gets `showPortionTier` when the set spans two or more tiers; when two
 * or more clashing rows share a tier state (both untiered, or the SAME tier)
 * but differ in serving size, the serving size is appended to their titles —
 * a tier cannot tell apart two picks of one item at one tier (a 1/2 Cup and a
 * 1 Cup mac, both K-8: PROD-1372). Two identical titles therefore never stand
 * side by side with nothing to tell them apart. Pure — the input array and its
 * rows are not mutated; order is preserved.
 */
export function markMultiPortionRows(
  deliverables: ReadonlyArray<Deliverable>,
  identityOf: (d: Deliverable) => string = deliverableIdentityNoPortion,
): Deliverable[] {
  // Per identity: the distinct tier states ('' = no tier), and per tier state
  // the distinct serving sizes. Two rows sharing an identity can still differ
  // by something the row already shows (packaging label, a tasting vs dry-run
  // badge), so a clash only counts as a PORTION clash when the tiers or the
  // serving sizes actually differ — otherwise a same-portion pair would be
  // renamed to "Fresh Apple — 1 each" twice, distinguishing nothing.
  const groups = new Map<string, { tierStates: Set<string>; servingsByTier: Map<string, Set<string>> }>()
  const identities = deliverables.map((d) => identityOf(d))
  identities.forEach((identity, i) => {
    const d = deliverables[i]!
    const tier = d.portionTier?.code ?? ''
    const entry = groups.get(identity) ?? { tierStates: new Set<string>(), servingsByTier: new Map() }
    entry.tierStates.add(tier)
    const servings = entry.servingsByTier.get(tier) ?? new Set<string>()
    servings.add(d.servingSize ?? '')
    entry.servingsByTier.set(tier, servings)
    groups.set(identity, entry)
  })
  return deliverables.map((d, i) => {
    const entry = groups.get(identities[i]!)!
    let out = d
    if (d.portionTier && entry.tierStates.size >= 2) {
      out = { ...out, showPortionTier: true }
    }
    if (d.servingSize && entry.servingsByTier.get(d.portionTier?.code ?? '')!.size >= 2) {
      out = { ...out, name: `${d.name} — ${d.servingSize}` }
    }
    return out
  })
}

/**
 * Project shipments into one aggregated `Campus` per destination school.
 * Each shipment is grouped into packages (one per BundleDeliverable);
 * identical packages then merge across meals/days, summing package count.
 */
/**
 * A line belonging to the campus's WEEKLY MILK drop: the campus is on a fixed
 * weekly milk delivery day AND the line is a STANDALONE milk-stamped serving
 * line. Mirrors the materializer's fan-out semantics exactly — only standalone
 * milk lines take the weekly milk delivery date; a milk pick inside a meal
 * bundle ships (and renders) with the meal, so bundled lines never match.
 */
export function isWeeklyMilkLine(
  shipment: Pick<KitchenShipmentSummary, 'destinationSchoolMilkDeliveryDayOfWeek'>,
  line: Pick<KitchenShipmentLineSummary, 'lineRole' | 'milkOptionCode' | 'bundleDeliverableId' | 'parentLineId'>,
): boolean {
  return (
    shipment.destinationSchoolMilkDeliveryDayOfWeek != null &&
    line.lineRole === 'SERVING' &&
    line.milkOptionCode != null &&
    line.bundleDeliverableId === null &&
    line.parentLineId === null
  )
}

/**
 * Identity of a shipment's order-group section (PROD-1296): the
 * `(dimension, value)` tuple the materializer stamped. Null when the shipment
 * is ungrouped (or the server predates the field).
 */
export function shipmentGroupKey(shipment: Pick<KitchenShipmentSummary, 'group'>): string | null {
  const g = shipment.group
  return g ? `${g.dimension}|${g.value}` : null
}

/** Order-group section descriptor collected per campus while aggregating. */
interface GroupBucket {
  key: string
  label: string
  sortOrder: number | null
}

/** Key of the per-(mealtime, group) meal tally. `groupKey` null = ungrouped shipment. */
function mealsKey(mealtime: Mealtime, groupKey: string | null): string {
  return `${mealtime}|${groupKey ?? ''}`
}

/**
 * Section order under a mealtime: authored band order (`sortOrder`) first,
 * label as the tiebreak, and the Unassigned section always last.
 */
function compareGroups(a: GroupBucket, b: GroupBucket): number {
  if (a.key === UNASSIGNED_GROUP_KEY) {
    return b.key === UNASSIGNED_GROUP_KEY ? 0 : 1
  }
  if (b.key === UNASSIGNED_GROUP_KEY) {
    return -1
  }
  if (a.sortOrder !== null && b.sortOrder !== null && a.sortOrder !== b.sortOrder) {
    return a.sortOrder - b.sortOrder
  }
  if (a.sortOrder !== null && b.sortOrder === null) {
    return -1
  }
  if (a.sortOrder === null && b.sortOrder !== null) {
    return 1
  }
  return a.label.localeCompare(b.label)
}

/**
 * One bulk package line with a bulk packing spec (PROD-1500, worked out by the
 * server from the catalog's package sizes and portion volumes), recorded per
 * campus while packages are added and planned once every shipment is in
 * (`resolvePackingTargets`). Only a FULLY specced deliverable records entries;
 * any other one marks its target incomplete instead.
 */
export interface PackingEntry {
  /** Lines share containers only within one partition: delivery day x mealtime
   *  x order group x dry run x field trip x holiday treat, as the server's
   *  `/packing` groups. Containers never travel between days. */
  partition: string
  /** Where the plan renders: `row:<rowKey>` or `milk:<milk row key>`. */
  target: string
  /** The line's spec from the server: its container, the servings per container and the foods it measured. */
  spec: BulkPackingSpec
  /** The line's servings (`plannedQuantity`). */
  servings: number
}

/** A target's packing once planned. */
export interface ResolvedPacking {
  /** The target's own plans; null when it owns none. */
  packing: BulkPackingPlan[] | null
  /** Targets whose containers hold this target's servings (it is "packed with" them). */
  packedWith: string[]
  /**
   * The target carries servings no shown plan covers (a deliverable or a day
   * with no spec, or a plan it shared with such a target): it shows no
   * containers and no "packed with" at all (today's display, D23).
   */
  incomplete: boolean
}

/**
 * Plan every packing group ONCE and hand each plan to one target. A group is
 * the lines of one partition that may share containers (`bulkPackingGroupKey`:
 * the same container, servings per container AND foods, so ½ cup and a #8
 * disher of corn pack together, while beef and Halal tacos at 20 a pan, or
 * green beans and corn at ½ cup, never share one): its lines' servings are
 * summed (OLHSA's 300 + 25 tacos at 20 a pan → 325 → fifteen 20s, then 13 and
 * 12), never planned per line. A group spanning two targets renders on the one
 * holding more of its servings (the first seen on a tie); the others are
 * "packed with" it.
 *
 * Never a wrong count (D23): a target in `incompleteTargets` (servings with no
 * spec) shows nothing, and every group with an incomplete member is dropped
 * for ALL its members, who become incomplete in turn (repeated until nothing
 * changes). So pans never cover servings on a row that shows none, and never
 * sit on a row whose quantity holds servings they do not cover.
 * A complete target's plans from several partitions (a weekly row spans five
 * delivery days, all of one row's foods) pool per way of packing
 * (`mergeBulkPackingPlans`). Pure.
 */
export function resolvePackingTargets(
  entries: ReadonlyArray<PackingEntry>,
  incompleteTargets: Iterable<string> = [],
): Map<string, ResolvedPacking> {
  interface PackingGroup {
    spec: BulkPackingSpec
    totalServings: number
    servingsByTarget: Map<string, number>
  }
  const groups = new Map<string, PackingGroup>()
  for (const entry of entries) {
    if (!(entry.servings > 0)) {
      continue
    }
    // Lines share containers only when they hold the same foods packed the same way.
    const key = `${entry.partition}|${bulkPackingGroupKey(entry.spec)}`
    const group = groups.get(key) ?? { spec: entry.spec, totalServings: 0, servingsByTarget: new Map<string, number>() }
    group.totalServings += entry.servings
    group.servingsByTarget.set(entry.target, (group.servingsByTarget.get(entry.target) ?? 0) + entry.servings)
    groups.set(key, group)
  }
  const incomplete = new Set(incompleteTargets)
  const live = new Set(groups.values())
  let changed = true
  while (changed) {
    changed = false
    for (const group of live) {
      const members = [...group.servingsByTarget.keys()]
      if (members.some((target) => incomplete.has(target))) {
        live.delete(group)
        for (const target of members) {
          incomplete.add(target)
        }
        changed = true
      }
    }
  }
  const plansByTarget = new Map<string, BulkPackingPlan[]>()
  const packedWith = new Map<string, Set<string>>()
  for (const group of live) {
    let owner: string | null = null
    for (const [target, servings] of group.servingsByTarget) {
      if (owner === null || servings > (group.servingsByTarget.get(owner) ?? 0)) {
        owner = target
      }
    }
    if (owner === null) {
      continue
    }
    plansByTarget.set(owner, [...(plansByTarget.get(owner) ?? []), planBulkPacking(group.totalServings, group.spec)])
    for (const target of group.servingsByTarget.keys()) {
      if (target !== owner) {
        packedWith.set(target, (packedWith.get(target) ?? new Set<string>()).add(owner))
      }
    }
  }
  const resolved = new Map<string, ResolvedPacking>()
  for (const target of incomplete) {
    resolved.set(target, { packing: null, packedWith: [], incomplete: true })
  }
  for (const target of new Set([...plansByTarget.keys(), ...packedWith.keys()])) {
    const plans = mergeBulkPackingPlans(plansByTarget.get(target) ?? [])
    resolved.set(target, {
      packing: plans.length > 0 ? plans : null,
      packedWith: [...(packedWith.get(target) ?? [])],
      incomplete: false,
    })
  }
  return resolved
}

/**
 * A package is MILK (PROD-1502) when every item in it carries a
 * `milkOptionCode`, condiments included, so a package that also holds anything
 * else stays on its meal row and every count it feeds stays where it was.
 */
export function isMilkPackage(components: ReadonlyArray<{ milkOptionCode: string | null }>): boolean {
  return components.length > 0 && components.every((c) => c.milkOptionCode !== null)
}

/** Milk card row key: the canonical milk option, split only by a serving that scales the recipe. */
function milkRowKey(code: string, servingMultiplier: number): string {
  return servingMultiplier === 1 ? code : `${code}|x${servingMultiplier}`
}

/** A milk card row while shipments are folded in. */
interface MilkBucket {
  line: Omit<CampusMilkLine, 'byMealtime' | 'byGroup' | 'serviceDates' | 'packing'>
  byMealtime: Map<Mealtime, number>
  /** Servings per `shipmentGroupKey` of the shipments feeding the row; null = ungrouped. */
  byGroup: Map<string | null, number>
  serviceDates: Set<string>
}

/** Fold `qty` servings of one milk item into the campus milk card; returns the row key. */
function addMilkRow(
  milk: Map<string, MilkBucket>,
  input: {
    code: string
    name: string
    component: Component
    qty: number
    mealtime: Mealtime
    /** `shipmentGroupKey` of the shipment the milk rides in. */
    groupKey: string | null
    serviceDay: string
    temperature: Temperature
    servingSize: string | null
    packaging: string | null
    packagingSkuKind: string | null
  },
): string {
  const key = milkRowKey(input.code, input.component.servingMultiplier ?? 1)
  const row = milk.get(key) ?? {
    line: {
      key,
      milkOptionCode: input.code,
      name: input.name,
      qty: 0,
      component: input.component,
      temperature: input.temperature,
      ...(input.servingSize ? { servingSize: input.servingSize } : {}),
      ...(input.packaging ? { packaging: input.packaging } : {}),
      ...(input.packaging && input.packagingSkuKind ? { packagingSkuKind: input.packagingSkuKind } : {}),
    },
    byMealtime: new Map<Mealtime, number>(),
    byGroup: new Map<string | null, number>(),
    serviceDates: new Set<string>(),
  }
  row.line.qty += input.qty
  row.byMealtime.set(input.mealtime, (row.byMealtime.get(input.mealtime) ?? 0) + input.qty)
  row.byGroup.set(input.groupKey, (row.byGroup.get(input.groupKey) ?? 0) + input.qty)
  row.serviceDates.add(input.serviceDay)
  milk.set(key, row)
  return key
}

/**
 * A milk row's per-order-group split (PROD-1624), in the mealtime sections'
 * order; null when no grouped shipment fed it. Our Lady of Mount Carmel takes
 * milk for PK3-5 and K-8 at separate drop-offs, and the summed card left the
 * vendor no way to split it.
 */
function milkGroupSplit(
  byGroup: ReadonlyMap<string | null, number>,
  groups: ReadonlyMap<string, GroupBucket>,
): CampusMilkGroupQty[] | null {
  if (![...byGroup.keys()].some((key) => key !== null)) {
    return null
  }
  return [...byGroup.entries()]
    .filter(([, qty]) => qty > 0)
    .map(([key, qty]) => ({
      bucket: (key !== null ? groups.get(key) : undefined) ?? {
        key: UNASSIGNED_GROUP_KEY,
        label: UNASSIGNED_GROUP_LABEL,
        sortOrder: null,
      },
      qty,
    }))
    .sort((a, b) => compareGroups(a.bucket, b.bucket))
    .map(({ bucket, qty }) => ({ key: bucket.key, label: bucket.label, qty }))
}

export function shipmentsToCampuses(shipments: ReadonlyArray<KitchenShipmentSummary>): Campus[] {
  interface CampusBucket {
    first: KitchenShipmentSummary
    /** True while EVERY shipment seen for this campus is a dry run. A
     *  destination that also has real service must not carry the warning. */
    allDryRun: boolean
    /** True while EVERY shipment seen is a TASTING — only consulted when
     *  `allDryRun` holds; a mixed campus keeps the generic dry-run label. */
    allTasting: boolean
    earliestServiceDate: string
    packages: Map<string, PackageBucket>
    /** SUPPLY lines (isSupply) summed per SKU across the campus's shipments. */
    supplies: Map<string, SupplyLine>
    /** PACKAGING (non-supply, non-bulk PACKAGE lines) summed per SKU. */
    packaging: Map<string, SupplyLine>
    /**
     * True meal count per (mealtime, order-group), summed across the campus's
     * shipments — see `mealsKey`. A shipment belongs to exactly ONE group, so
     * the per-shipment MAX-per-bundle-then-sum tally below lands whole in one
     * entry; the mealtime total is the sum of its groups' entries. Keying on
     * mealtime alone would make a per-group count either a sum-of-max across
     * bands (double count) or a re-derivation from merged rows (PROD-1296).
     */
    meals: Map<string, number>
    /** Order-group sections seen on this campus, by `shipmentGroupKey`. */
    groups: Map<string, GroupBucket>
    /** Mealtimes with at least one GROUPED shipment — only those render sections. */
    groupedMealtimes: Set<Mealtime>
    /** Distinct SERVICE day keys per mealtime — feeds `MealtimeBlock.serveDays`. */
    serveDays: Map<Mealtime, Set<string>>
    /** The milk card (PROD-1502): weekly milk drop lines and milk packages,
     *  per canonical milk option code, summed across ALL of the campus's
     *  shipments and mealtimes. */
    milk: Map<string, MilkBucket>
    /** Bulk package lines with a spec, planned once every shipment is in. */
    packingEntries: PackingEntry[]
    /** Targets (`row:` / `milk:`) fed servings no spec covers: they show no plan. */
    incompletePacking: Set<string>
  }
  const bySchool = new Map<string, CampusBucket>()

  for (const shipment of shipments) {
    const bucket = bySchool.get(shipment.schoolLocationId) ?? {
      first: shipment,
      allDryRun: true,
      allTasting: true,
      earliestServiceDate: shipment.serviceDate,
      packages: new Map<string, PackageBucket>(),
      supplies: new Map<string, SupplyLine>(),
      packaging: new Map<string, SupplyLine>(),
      meals: new Map<string, number>(),
      groups: new Map<string, GroupBucket>(),
      groupedMealtimes: new Set<Mealtime>(),
      serveDays: new Map<Mealtime, Set<string>>(),
      milk: new Map<string, MilkBucket>(),
      packingEntries: [],
      incompletePacking: new Set<string>(),
    }
    if (shipment.serviceDate < bucket.earliestServiceDate) {
      bucket.earliestServiceDate = shipment.serviceDate
    }
    bucket.allDryRun = bucket.allDryRun && shipment.isDryRun === true
    bucket.allTasting = bucket.allTasting && shipment.dryRunServiceType === 'TASTING'
    const mealtime = clientMealtime(shipment.mealtime)
    // Order-group section (PROD-1296). Every package and the meal tally of
    // this shipment land in this one section.
    const groupKey = shipmentGroupKey(shipment)
    if (groupKey !== null && shipment.group) {
      bucket.groupedMealtimes.add(mealtime)
      if (!bucket.groups.has(groupKey)) {
        bucket.groups.set(groupKey, {
          key: groupKey,
          label: shipment.group.label,
          sortOrder: shipment.group.sortOrder ?? null,
        })
      }
    }

    // Meal tally for THIS shipment, accumulated as packages are added — i.e.
    // BEFORE identical packages merge across bundles/meals/days. That merge is
    // deliberate and correct for prep quantities ("205 pancakes" really is what
    // the kitchen makes), but it collapses bundle identity, the service-date
    // axis, and the bundle/standalone split all at once. None of that survives
    // into the rendered rows, so a meal count derived from them is unsound —
    // which is exactly how a 205-meal lunch came to render as 210 (or 405,
    // depending on the order Postgres returned the lines in).
    const bundleMeals = new Map<string, number>()
    let standaloneMeals = 0

    // 1. Within this shipment, gather component leaf lines per
    //    BundleDeliverable, and the deliverable-level lines (the packages,
    //    carrying the package count).
    const leavesByDeliv = new Map<string, KitchenShipmentLineSummary[]>()
    const packageLines: KitchenShipmentLineSummary[] = []
    for (const line of shipment.lines) {
      // Weekly milk lines leave the deliverable tree entirely — they render as
      // the campus-level "Weekly milk" section (per-SKU carton counts), never
      // as mealtime deliverable rows, and they must not feed the merge keys,
      // supplies, or meal tallies below.
      if (isWeeklyMilkLine(shipment, line)) {
        const code = normalizeMilkOptionCode(line.milkOptionCode) ?? line.milkOptionCode!
        const name = line.componentName ?? milkOptionLabel(code)
        const item = toPackageComponent(line, line.plannedQuantity)
        const milkKey = addMilkRow(bucket.milk, {
          code,
          name,
          // Projected like a package component so the print packet's
          // ingredient list + recipe pack can fold milk demand in.
          component: { ...bucketToComponent(item), name },
          qty: line.plannedQuantity,
          mealtime,
          groupKey,
          serviceDay: shipmentDayKey(shipment, 'SERVICE'),
          temperature: temperatureFor(line.temperatureClass),
          servingSize: item.recipeServing,
          packaging: null,
          packagingSkuKind: null,
        })
        // Loose weekly cartons: no spec covers them, so the row shows no containers.
        bucket.incompletePacking.add(`milk:${milkKey}`)
        continue
      }
      if (line.lineRole === 'SERVING') {
        // Standalone (non-bundle) lines key by full portion identity, not just
        // componentId — otherwise the same component ordered at two portion
        // sizes (e.g. per grade tier) collapses into one package with duplicate
        // components ("Mustard Packet, Mustard Packet").
        // À-la-carte PACKAGED lines (menu-template packaging) carry no
        // bundleDeliverableId — they group with their containing package line
        // through parentLineId.
        // A derived holiday treat keeps the `solo:` semantics (sum, portion
        // identity) but never shares a group with a real order line for the
        // same product — the kitchen packs the free treats as their own row.
        const key =
          line.bundleDeliverableId ??
          (line.parentLineId
            ? `pkg:${line.parentLineId}`
            : `solo:${lineIsHolidayTreat(line) ? 'treat:' : ''}${componentIdentity(line)}`)
        const list = leavesByDeliv.get(key) ?? []
        list.push(line)
        leavesByDeliv.set(key, list)
      } else if (line.lineRole === 'PACKAGE') {
        // Collect EVERY package row. Synthetic à-la-carte packages
        // (menu-template packaging) carry bundleDeliverableId null, and a
        // packaging-less bulk one also has packagingSkuId null — the old
        // `bundleDeliverableId || packagingSkuId` predicate dropped those
        // entirely, sending their leaves down the lossy legacy synthesis
        // path where the package count is re-derived instead of read.
        //
        // Operator-added non-order items (PROD-1250) land here too and that is
        // SAFE without a filter: they carry no bundleDeliverableId, so each
        // keys a `pkg:<id>` group of its own with no leaves, and `addPackage`
        // early-returns on `leaves.length === 0`. They therefore never become a
        // deliverable or feed the meal count, while still being tallied onto
        // the campus supplies pull below. `extra-non-order-lines` in
        // shipment-to-campus.test.ts pins that.
        packageLines.push(line)
      }
      // Packaging/supply SKU counts (trays, utensils, napkins…) ride the
      // PACKAGE lines — tally them per campus so kitchens can pull stock.
      // Bulk primary lines stay OUT of the packaging-supply rollup. Their
      // plannedQuantity is a SERVING count (quantities are servings
      // everywhere now), and servings are not a pan count — Ordo has no
      // pan/tray map, so the number of pans simply isn't derivable. Counting
      // servings here would read as "100 pans".
      // SUPPLIES (disposables shipped with the meal) and PACKAGING (what the
      // food is served in) are categorically separate pulls — `isSupply`
      // decides the bucket, never the SKU's kind.
      // A Section with a bulk packing spec stays out too: its containers come
      // from its plan (PROD-1500), and its quantity is servings.
      if (
        line.lineRole === 'PACKAGE' &&
        line.packagingSkuId &&
        line.plannedQuantity > 0 &&
        line.isBulk !== true &&
        !line.bulkPacking
      ) {
        tallySupplyLine(line.isSupply === true ? bucket.supplies : bucket.packaging, line)
      }
    }

    // 2. Each deliverable-level line is one package instance. Fall back to
    //    synthesizing a package from any leaf group with no package line.
    const consumed = new Set<string>()
    const addPackage = (
      key: string,
      temperatureClass: KitchenShipmentLineSummary['temperatureClass'],
      count: number,
      isFieldTrip: boolean,
      packaging: string | null,
      isBulkPrep = false,
      packagingImageUrl: string | null = null,
      supplies: SupplyLine[] = [],
      packagingSkuKind: string | null = null,
      packed: ReadonlyArray<KitchenShipmentLineSummary> = [],
      fullySpecced = false,
      sections: DeliverableSectionPlan[] | null = null,
    ): void => {
      const isDryRun = shipment.isDryRun === true
      const dryRunServiceType = isDryRun ? (shipment.dryRunServiceType ?? 'DRY_RUN') : null
      const deliverCold = shipment.deliverCold === true
      const leaves = leavesByDeliv.get(key) ?? []
      if (leaves.length === 0 || count <= 0) {
        return
      }
      consumed.add(key)
      const isSolo = key.startsWith('solo:')
      // A derived holiday treat is a standalone `solo:` leaf with no order
      // behind it: it renders as its own badged row for packing but must never
      // merge into a regular row of the same item, nor count as a meal.
      const isHolidayTreat = leaves.some(lineIsHolidayTreat)
      // A solo group can carry duplicate same-identity leaves when the same
      // component + portion is ordered by more than one source (e.g. a school
      // forecast line and a parent line on a hybrid campus). A bundle-deliverable
      // group instead carries one leaf PER GRADE TIER, differing only in the
      // portion. Dedupe by the identity that fits the group so the package
      // renders one component ("Corn", not "Corn, Corn") either way.
      const identityOf = isSolo ? componentIdentity : componentIdentityNoPortion
      // A tier with no variant stamped is the same food as a tier of that component carrying its variant (Oct 23:
      // Wade Thomas's calzone tiers read `null` and `cvar_c031_standard`), so it never adds a second copy: the row
      // read "Calzone, Calzone" and printed "2 Calzone".
      const variantStamped = new Set(leaves.filter((l) => l.componentVariantId).map((l) => l.componentId))
      const seenIdentity = new Set<string>()
      const uniqueLeaves = leaves.filter((l) => {
        if (!isSolo && !l.componentVariantId && variantStamped.has(l.componentId)) {
          return false
        }
        const identity = identityOf(l)
        if (seenIdentity.has(identity)) {
          return false
        }
        seenIdentity.add(identity)
        return true
      })
      const components = uniqueLeaves.map((leaf) => toPackageComponent(leaf, count))
      // The row key keeps the FULL identity (portion included) so merge
      // semantics are untouched — only the dedupe above is portion-agnostic.
      const identitySet = uniqueLeaves.map(componentIdentity).sort().join(',')
      // Field-trip packages are keyed apart from identical regular ones —
      // bagged and plated servings are physically different prep work. Bulk
      // packages likewise: bulk-tray prep and plated packing don't merge.
      // Two independent identity refinements, both kept:
      //  - PACKAGING: rows merge only when the contents AND the physical
      //    package match (the same item in a Bulk Delivery Box and on a tray
      //    are different pulls), so a merge never silently adopts the first
      //    row's packaging label for the second's.
      //  - DRY RUN: a test cook and a live service are different work even
      //    when the food matches, so they never collapse into one row.
      // The service TYPE is part of the dry-run key: a tasting and a dry run
      // of the same food carry different labels, so they never share a row.
      // ORDER GROUP (PROD-1296): a Preschool apple and a K-8 apple are packed
      // into different bins, so the band leads the key and identical packages
      // merge only WITHIN a band.
      // SECTIONS: the same bag with the oranges in one cup or in two is packed
      // differently, so the inner packages are part of the key too.
      const rowKey = `${groupKey ?? ''}|${mealtime}|${temperatureClass ?? ''}|${isFieldTrip ? 'field-trip' : ''}|${isHolidayTreat ? 'treat' : ''}|${isBulkPrep ? 'bulk' : ''}|${packaging ?? ''}|${isDryRun ? `dry-run:${dryRunServiceType}` : ''}|${deliverCold ? 'cold' : ''}|${identitySet}|${deliverableSectionsKey(sections)}`
      // Name the package by its contents (consistent for identical packages
      // and more informative than a FROM_GROUP slot label like "Beverage"),
      // through the canonical bundle auto-title system so a deliverable titles
      // exactly like the bundle it came from: component-kind order, ' & '
      // within a kind, ' • ' between kinds, beverages/milk last. Condiments
      // are excluded from the title and carried as a "Condiments: …" sublabel
      // (they stay in the title only when the package is all condiments).
      const titleEntries = components.map((c) => ({ name: c.name, kind: c.componentKind }))
      const name = buildBundleAutoTitle(titleEntries)
      // An all-condiments package keeps its condiments IN the title (shared
      // builder rule) — no sublabel, or the row would say the same thing twice.
      const condiments = titleEntries.some((e) => e.kind !== 'CONDIMENT')
        ? buildBundleCondimentDescription(titleEntries)
        : null

      // Tally meals here, above the create/merge branch, so a package that
      // merges into an existing row still contributes — that is what makes the
      // total independent of the order the server returned the lines in.
      // Every deliverable of a bundle ships for the SAME orders (the
      // materializer emits one unit per deliverable, all sharing the order
      // count), so a bundle takes the MAX across its deliverables, never a sum.
      // Bundle-less lines count only when they are a main entree — a solo milk
      // or condiment is not a meal. Legacy rows that have a deliverable but no
      // bundle key off the deliverable so a multi-deliverable group can't be
      // counted once per deliverable.
      const mealKey = leaves[0]?.bundleId ?? (isSolo ? null : `deliv:${key}`)
      if (isHolidayTreat) {
        // Free with a meal that is already counted — never a meal itself.
      } else if (mealKey !== null) {
        bundleMeals.set(mealKey, Math.max(bundleMeals.get(mealKey) ?? 0, count))
      } else if (packageProductType(packageComponentTypes(components)) === 'main') {
        standaloneMeals += count
      }

      // Package lines the server gave a bulk packing spec are planned per
      // campus x delivery day x mealtime x order group x dry run x field trip x
      // holiday treat once every shipment is in (PROD-1500). Only a FULLY
      // specced deliverable plans; any other one marks its target incomplete,
      // so that row or milk line never shows containers covering part of its
      // servings (D23).
      const recordPacking = (target: string): void => {
        if (!fullySpecced) {
          bucket.incompletePacking.add(target)
          return
        }
        const partition = [
          shipmentDayKey(shipment, 'DELIVERY'),
          mealtime,
          groupKey ?? '',
          isDryRun ? 'dry-run' : '',
          isFieldTrip ? 'field-trip' : '',
          isHolidayTreat ? 'treat' : '',
          // A milk row has no "packed with" line, so its containers are never
          // shared with another row: each milk row plans its own servings.
          target.startsWith('milk:') ? target : '',
        ].join('|')
        for (const line of packed) {
          if (line.bulkPacking && line.plannedQuantity > 0) {
            bucket.packingEntries.push({ partition, target, spec: line.bulkPacking, servings: line.plannedQuantity })
          }
        }
      }

      // Milk leaves the mealtime cards for the campus milk card (PROD-1502).
      // The meal tally above already ran, so meal counts do not move. Bagged
      // (field-trip) milk travels in the bags with the meal, and dry-run milk
      // stays with its badged test cook.
      if (!isFieldTrip && !isDryRun && !isHolidayTreat && isMilkPackage(components)) {
        const serviceDay = shipmentDayKey(shipment, 'SERVICE')
        const milkKeys = components.map((c) =>
          addMilkRow(bucket.milk, {
            code: normalizeMilkOptionCode(c.milkOptionCode) ?? c.milkOptionCode!,
            name: c.name,
            component: bucketToComponent(c),
            qty: count,
            mealtime,
            groupKey,
            serviceDay,
            temperature: temperatureFor(temperatureClass),
            servingSize: c.recipeServing,
            packaging,
            packagingSkuKind,
          }),
        )
        // One spec packs the whole package. A one-milk package's plan goes on
        // its milk row; a package of two milks would put one plan's
        // containers beside two rows' servings, so those rows show none.
        if (milkKeys.length === 1) {
          recordPacking(`milk:${milkKeys[0]}`)
        } else {
          for (const milkKey of milkKeys) {
            bucket.incompletePacking.add(`milk:${milkKey}`)
          }
        }
        return
      }
      recordPacking(`row:${rowKey}`)

      const portionTier = unanimousPortionTier(leaves)
      const existing = bucket.packages.get(rowKey)
      if (existing) {
        existing.qty += count
        existing.supplies = mergeSupplyLines(existing.supplies, supplies)
        // A merged row keeps its tier only while every contributor agrees.
        if (existing.portionTier?.code !== portionTier?.code) {
          existing.portionTier = null
        }
      } else {
        bucket.packages.set(rowKey, {
          rowKey,
          mealtime,
          groupKey,
          groupLabel: shipment.group?.label ?? null,
          name,
          condiments,
          temperatureClass,
          qty: count,
          packaging,
          packagingImageUrl,
          packagingSkuKind,
          sections,
          components,
          isFieldTrip,
          isHolidayTreat,
          isDryRun,
          dryRunServiceType,
          isBulkPrep,
          deliverCold,
          supplies: mergeSupplyLines([], supplies),
          portionTier,
        })
      }
    }

    // Group PACKAGE lines per deliverable. A deliverable's ROOT package lines
    // are its distinct GRADE TIERS (the materializer keys aggregates by
    // `deliverableId|fingerprint|grade|gradeRangeId`) and their counts SUM.
    // Every other PACKAGE line is a nested packaging row — a lid, a napkin, a
    // condiment cup — whose quantity is a MULTIPLE of its parent's and which
    // must never feed the order count. `parentLineId` marks those exactly (the
    // server's temp-log path already keys off it); inferring primacy from
    // `quantityBasis` alone let a lidded fruit cup read 80x on a 40-meal order
    // and, via the MAX-based meal tally, print "80 Meals" (PROD-1000).
    const pkgLinesByDeliv = new Map<string, KitchenShipmentLineSummary[]>()
    for (const pkg of packageLines) {
      // À-la-carte package lines have no deliverable id — their leaves keyed
      // themselves `pkg:<packageLineId>` above.
      const key = pkg.bundleDeliverableId ?? `pkg:${pkg.id}`
      const list = pkgLinesByDeliv.get(key) ?? []
      list.push(pkg)
      pkgLinesByDeliv.set(key, list)
    }
    for (const [delivKey, allPkgs] of pkgLinesByDeliv) {
      const leaves = leavesByDeliv.get(delivKey) ?? []
      // Roots only, so a nested row can never be mistaken for a package —
      // including a legacy one, where the `quantityBasis` filter finds no
      // marker anywhere and the whole group would otherwise stand in.
      const rootPkgs = allPkgs.filter((p) => p.parentLineId === null)
      const candidates = rootPkgs.length > 0 ? rootPkgs : allPkgs
      const primaries = candidates.filter((p) => parseQuantityBasis(p.quantityBasis) !== null)
      const pkgs = primaries.length > 0 ? primaries : candidates
      const bases = pkgs.map((p) => parseQuantityBasis(p.quantityBasis))
      // Field-trip flags and packaging SKU names can ride a child line, so both
      // are read off the full group, not just the primaries.
      const isFieldTrip = allPkgs.some(lineIsFieldTrip) || leaves.some(lineIsFieldTrip)
      // Packaging label (PROD-174): the physical SKU chefs pack into, else
      // the deliverable's own name as a coarser fallback.
      // One `find` for both: the photo must come off the very line that
      // supplied the label. Picking "first line with a photo" separately would
      // happily show SKU B's container next to SKU A's name.
      // Only a ROOT line can name the container. Nested rows are lids, cutlery,
      // napkins, supplies — a SKU-less root (bare bulk pan, no-packaging
      // deliverable) must fall back to the deliverable's name, not to the first
      // nested SKU it finds ("Spork & Napkin" as the melon's packaging).
      const packagingLine = candidates.find(
        (p) => p.packagingSkuName !== null && p.isSupply !== true && p.packagingKind !== 'UTENSIL',
      )
      const packaging = packagingLine?.packagingSkuName ?? allPkgs[0]?.bundleDeliverableName ?? null
      // The name fallback (`bundleDeliverableName`) names no SKU, so it has no photo.
      const packagingImageUrl = packagingLine?.packagingImageUri ?? null
      // Same line again: the kind must describe the SKU the label names.
      const packagingSkuKind = packagingLine?.packagingSkuKind ?? null
      // Bulk packing (PROD-1500): the deliverable plans only when FULLY
      // specced — every root that counts toward the row has a spec and plans
      // exactly the servings it counts (a legacy row whose stored quantity is
      // not its servings never plans), and every Section the server marked as
      // a target has one too. Anything less and its row shows no containers,
      // rather than containers covering part of its servings (D23).
      const sections = allPkgs.filter(
        (p) => p.parentLineId !== null && p.bulkPackingTarget === true && p.plannedQuantity > 0,
      )
      const fullySpecced =
        pkgs.length > 0 &&
        pkgs.every((p) => p.bulkPacking && parseQuantityBasis(p.quantityBasis)?.servingCount === p.plannedQuantity) &&
        sections.every((p) => p.bulkPacking)
      const packed = fullySpecced ? [...pkgs, ...sections] : []
      const servingCount = bases.reduce((sum, b) => sum + (b?.servingCount ?? 0), 0)
      // Supplies nested in this deliverable's package tree (already
      // package-multiplied by the materializer).
      const supplies = supplyLinesFrom(allPkgs.filter((p) => p.isSupply === true))
      if (allPkgs.some((p) => p.isBulk === true)) {
        // Straight-serve (bulk) prep: the display/package count is the
        // serving count, same as plated.
        addPackage(
          delivKey,
          pkgs[0]!.temperatureClass,
          servingCount,
          isFieldTrip,
          packaging,
          true,
          packagingImageUrl,
          supplies,
          packagingSkuKind,
          packed,
          fullySpecced,
        )
      } else {
        // Plated: the materializer sets packageCount === servingCount, so this
        // matches the primaries' plannedQuantity — it only differs by excluding
        // the nested packaging rows.
        const count = servingCount > 0 ? servingCount : pkgs.reduce((sum, p) => sum + p.plannedQuantity, 0)
        addPackage(
          delivKey,
          pkgs[0]!.temperatureClass,
          count,
          isFieldTrip,
          packaging,
          false,
          packagingImageUrl,
          supplies,
          packagingSkuKind,
          packed,
          fullySpecced,
          planDeliverableSections(allPkgs, leaves),
        )
      }
    }
    // Leaf groups with no deliverable-level line (legacy/edge data): treat
    // them as standalone packages, counted off their leaf quantity.
    for (const [key, leaves] of leavesByDeliv) {
      if (consumed.has(key)) {
        continue
      }
      // A `solo:` group (one standalone component keyed by portion identity)
      // holds distinct order sources for the same item → SUM. A bundleDeliverable
      // group with no package line holds the package's DISTINCT components, each
      // carrying the shared package count → one leaf's quantity (max), not sum.
      const count = key.startsWith('solo:')
        ? leaves.reduce((sum, l) => sum + l.plannedQuantity, 0)
        : Math.max(...leaves.map((l) => l.plannedQuantity), 0)
      addPackage(
        key,
        leaves[0]?.temperatureClass ?? null,
        count,
        leaves.some(lineIsFieldTrip),
        leaves[0]?.bundleDeliverableName ?? null,
      )
    }

    // Fold this shipment's meals into the campus. Summing per shipment keeps a
    // multi-day range correct (each service date contributes its own orders)
    // without the per-day print rebuild double-counting anything.
    // Milk-only weekly shipments (a Monday drop covering Tue–Fri service) fold
    // NOTHING here — their service days would otherwise leak into the milk
    // day's serve-day badge, and their mealtime would seed a phantom entry.
    const hasNonMilkServing = shipment.lines.some((l) => l.lineRole === 'SERVING' && !isWeeklyMilkLine(shipment, l))
    if (hasNonMilkServing) {
      let shipmentMeals = standaloneMeals
      for (const qty of bundleMeals.values()) {
        shipmentMeals += qty
      }
      const mk = mealsKey(mealtime, groupKey)
      bucket.meals.set(mk, (bucket.meals.get(mk) ?? 0) + shipmentMeals)
      // SERVICE axis, deliberately, on a delivery-bucketed card: the badge
      // answers "when does this food hit trays", which is the one service-side
      // fact a chef needs after the views flipped to delivery days.
      const serveSet = bucket.serveDays.get(mealtime) ?? new Set<string>()
      serveSet.add(shipmentDayKey(shipment, 'SERVICE'))
      bucket.serveDays.set(mealtime, serveSet)
    }
    bySchool.set(shipment.schoolLocationId, bucket)
  }

  const campuses: Campus[] = []
  for (const bucket of bySchool.values()) {
    const blocks: MealtimeBlock[] = []
    /** Projected rows by row key — where the packing plans attach. */
    const rowsByKey = new Map<string, Deliverable>()
    for (const mealtime of MEALTIME_ORDER) {
      const packages = Array.from(bucket.packages.values())
        .filter((p) => p.mealtime === mealtime && p.qty > 0)
        .sort((a, b) => a.name.localeCompare(b.name))
      // Same index order as `packages` — the group sectioning below pairs
      // `packages[i]` with `deliverables[i]`.
      const deliverables: Deliverable[] = markMultiPortionRows(
        packages.map((p) => projectPackage(p, bucket.first.schoolLocationId)),
      )
      if (deliverables.length === 0) {
        continue
      }
      packages.forEach((p, i) => rowsByKey.set(p.rowKey, deliverables[i]!))
      // Mealtime total = the sum of its (mealtime, group) tallies. Identical
      // to the old per-mealtime tally on an ungrouped campus (one entry).
      let meals = 0
      for (const [key, qty] of bucket.meals) {
        if (key.startsWith(`${mealtime}|`)) {
          meals += qty
        }
      }
      // Order-group sections (PROD-1296), only where a grouped shipment feeds
      // this mealtime. Rows from an ungrouped shipment on such a campus go to
      // an "Unassigned" section rather than merging into a band.
      let groups: MealtimeGroup[] | undefined
      if (bucket.groupedMealtimes.has(mealtime)) {
        const sections = new Map<
          string,
          { bucket: GroupBucket; groupKey: string | null; deliverables: Deliverable[] }
        >()
        packages.forEach((p, i) => {
          const sectionKey = p.groupKey ?? UNASSIGNED_GROUP_KEY
          const section = sections.get(sectionKey) ?? {
            bucket: bucket.groups.get(sectionKey) ?? {
              key: UNASSIGNED_GROUP_KEY,
              label: UNASSIGNED_GROUP_LABEL,
              sortOrder: null,
            },
            groupKey: p.groupKey,
            deliverables: [],
          }
          const row = deliverables[i]!
          if (p.groupKey === null) {
            row.groupKey = UNASSIGNED_GROUP_KEY
            row.groupLabel = UNASSIGNED_GROUP_LABEL
          }
          section.deliverables.push(row)
          sections.set(sectionKey, section)
        })
        const sectioned = Array.from(sections.values())
          .sort((a, b) => compareGroups(a.bucket, b.bucket))
          .map((section) => ({
            key: section.bucket.key,
            label: section.bucket.label,
            meals: bucket.meals.get(mealsKey(mealtime, section.groupKey)) ?? 0,
            deliverables: section.deliverables,
          }))
        // A section header only earns its place when the mealtime is actually
        // split: a campus whose school orders in ONE grade-range column (every
        // school-forecast campus) would otherwise show "K-8 · 123 Meals" under
        // "Lunch · 123 Meals" — a header that restates the mealtime (Sheila
        // 2026-09-11: "only show those if grade range is grouping").
        groups = sectioned.length > 1 ? sectioned : undefined
      }
      blocks.push({
        mealtime,
        deliverables,
        meals,
        ...(groups ? { groups } : {}),
        serveDays: Array.from(bucket.serveDays.get(mealtime) ?? []).sort(),
        menuInstructions: [
          ...new Map(
            shipments
              .filter(
                (shipment) =>
                  shipment.schoolLocationId === bucket.first.schoolLocationId &&
                  clientMealtime(shipment.mealtime) === mealtime,
              )
              .flatMap((shipment) => (shipment.menuInstructions ?? []).map((note) => [note.menuId, note] as const)),
          ).values(),
        ],
        ...specialDietsOf(
          shipments.filter(
            (shipment) =>
              shipment.schoolLocationId === bucket.first.schoolLocationId &&
              clientMealtime(shipment.mealtime) === mealtime,
          ),
        ),
      })
    }
    // Packing (PROD-1500): plan each group once, attach it to the row holding
    // most of its servings; the others say which row they are packed with. A
    // row with servings no spec covers shows none of it (and is marked, so a
    // cross-school merge with it shows none either).
    const packingByTarget = resolvePackingTargets(bucket.packingEntries, bucket.incompletePacking)
    const targetName = (target: string): string | null =>
      target.startsWith('row:')
        ? (rowsByKey.get(target.slice('row:'.length))?.name ?? null)
        : (bucket.milk.get(target.slice('milk:'.length))?.line.name ?? null)
    for (const [rowKey, row] of rowsByKey) {
      const resolved = packingByTarget.get(`row:${rowKey}`)
      if (!resolved) {
        continue
      }
      if (resolved.incomplete) {
        if (row.bulk) {
          row.packingIncomplete = true
        }
        continue
      }
      if (resolved.packing) {
        row.packing = resolved.packing
      }
      const packedWith = resolved.packedWith.map(targetName).filter((name): name is string => name !== null)
      if (packedWith.length > 0) {
        row.packedWith = packedWith
      }
    }
    // Milk card rows, canonical lineup order (extras appended); a scaled
    // serving's `|x<m>` row sorts beside its code.
    const milkKeys = Array.from(bucket.milk.keys())
    const milkRows: CampusMilkLine[] = orderedMilkOptionCodes([
      ...new Set(milkKeys.map((key) => bucket.milk.get(key)!.line.milkOptionCode)),
    ])
      .flatMap((code) => milkKeys.filter((key) => bucket.milk.get(key)!.line.milkOptionCode === code))
      .map((key) => bucket.milk.get(key)!)
      .filter((row) => row.line.qty > 0)
      .map((row) => {
        const packing = packingByTarget.get(`milk:${row.line.key}`)?.packing ?? null
        const byMealtime: CampusMilkMealtimeQty[] = MEALTIME_ORDER.filter((m) => row.byMealtime.has(m)).map((m) => ({
          mealtime: m,
          qty: row.byMealtime.get(m)!,
        }))
        const byGroup = milkGroupSplit(row.byGroup, bucket.groups)
        return {
          ...row.line,
          byMealtime,
          ...(byGroup ? { byGroup } : {}),
          serviceDates: Array.from(row.serviceDates).sort(),
          ...(packing ? { packing } : {}),
        }
      })
    // A milk-only day (the weekly drop with no meals riding along) must still
    // emit the campus — the milk section is the whole card that day.
    if (blocks.length === 0 && milkRows.length === 0) {
      continue
    }
    const s = bucket.first
    const addr = s.destinationSchoolAddress
    campuses.push({
      id: s.schoolLocationId,
      organizationName: s.destinationSchoolOrganizationName ?? s.destinationSchoolName,
      name: s.destinationSchoolName,
      ...(bucket.allDryRun
        ? { isDryRun: true, dryRunServiceType: bucket.allTasting ? ('TASTING' as const) : ('DRY_RUN' as const) }
        : {}),
      info: {
        addressLine1: addr?.line1 ?? '',
        city: addr?.city ?? '',
        state: addr?.stateProvince ?? '',
        zip: addr?.postalCode ?? '',
        pocName: s.contactName ?? '',
        phone: s.contactPhone ?? '',
        deliveryWindow: s.deliveryWindow ?? '',
        lunchStart: '',
        parking: s.parkingInstructions ?? '',
        notes: '',
      },
      mealtimes: blocks,
      // Pull-list order: by kind (utensils, plates, trays & boats,
      // containers), compostable stock first within a kind, then by name.
      supplies: Array.from(bucket.supplies.values()).sort(compareServiceMaterials),
      packaging: Array.from(bucket.packaging.values()).sort(compareServiceMaterials),
      milk: milkRows,
      // Synthesized-neutral: real shipments mean the order WAS placed,
      // so the campus reads as submitted (no missed-deadline banner).
      orderStatus: {
        deadline: bucket.earliestServiceDate,
        submittedAt: bucket.earliestServiceDate,
      },
    })
  }
  return campuses.sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Shipments whose DELIVERY date falls on the `YYYY-MM-DD` day.
 *
 * This backs the chef day view. A kitchen's day is what leaves the building:
 * on the service axis a Friday-delivered bagged lunch vanished from Friday's
 * list and reappeared on Monday, after it had already been cooked and loaded.
 */
export function shipmentsForDay(
  shipments: ReadonlyArray<KitchenShipmentSummary>,
  isoDay: string,
): KitchenShipmentSummary[] {
  return shipments.filter((s) => shipmentDayKey(s, 'DELIVERY') === isoDay)
}

/**
 * Per-date service status derived from shipment presence: a campus is
 * "off" on a date when it has no shipment that day. Mirrors the
 * `computeServiceStatus` contract the service-day builder consumes.
 */
export function serviceStatusFromShipments(
  campuses: ReadonlyArray<Campus>,
  shipments: ReadonlyArray<KitchenShipmentSummary>,
): (date: Date) => { status: 'full' | 'partial' | 'none'; offCampuses: Campus[] } {
  const datesBySchool = new Map<string, Set<string>>()
  for (const s of shipments) {
    const set = datesBySchool.get(s.schoolLocationId) ?? new Set<string>()
    // Delivery axis — this drives the CHEF calendar's "is this campus on
    // today" dots, which must agree with the day list beside them.
    set.add(shipmentDayKey(s, 'DELIVERY'))
    datesBySchool.set(s.schoolLocationId, set)
  }
  return (date: Date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const iso = `${y}-${m}-${d}`
    const offCampuses = campuses.filter((c) => !datesBySchool.get(c.id)?.has(iso))
    if (campuses.length === 0 || offCampuses.length === campuses.length) {
      return { status: 'none', offCampuses: [...campuses] }
    }
    return offCampuses.length === 0 ? { status: 'full', offCampuses: [] } : { status: 'partial', offCampuses }
  }
}

/**
 * A mealtime block's special-diet meals across its shipments, the same meal for the same diet summed; the key is
 * left off when there are none, as cached pre-field payloads have none.
 */
export function specialDietsOf(shipments: ReadonlyArray<Pick<KitchenShipmentSummary, 'specialDiets'>>): {
  specialDiets?: SpecialDietItem[]
} {
  const items = new Map<string, SpecialDietItem>()
  for (const item of shipments.flatMap((shipment) => shipment.specialDiets ?? [])) {
    const key = `${item.name}|${item.restrictions.join(',')}`
    const merged = items.get(key) ?? { ...item, quantity: 0 }
    merged.quantity += item.quantity
    items.set(key, merged)
  }
  return items.size > 0 ? { specialDiets: [...items.values()] } : {}
}
